import { Factor, maxWeightsSum, Option, sumFactorWeights, weightToBucket } from './choices';
import { RockContainer, VizContainerProps, VizInner } from './Rocks';

export const identityOptions: Option[] = [
    {
        id: 'race',
        label: 'Racial Identity',
        options: [
            { value: 'white', label: 'White', weight: 0 },
            { value: 'black', label: 'Black/African American', weight: 3 },
            { value: 'indigenous', label: 'Indigenous/Native American', weight: 4 },
            { value: 'asian', label: 'Asian', weight: 2 },
            { value: 'latinx', label: 'Latinx/Hispanic', weight: 2 },
            { value: 'middle_eastern', label: 'Middle Eastern/North African', weight: 3 },
            { value: 'multiracial', label: 'Multiracial', weight: 2 },
        ]
    },
    {
        id: 'gender',
        label: 'Gender Identity',
        options: [
            { value: 'cisgender_man', label: 'Cisgender Man', weight: 0 },
            { value: 'cisgender_woman', label: 'Cisgender Woman', weight: 2 },
            { value: 'transgender', label: 'Transgender', weight: 4 },
            { value: 'nonbinary', label: 'Non-binary/Genderqueer', weight: 3 },
            { value: 'gender_fluid', label: 'Gender Fluid', weight: 3 },
        ]
    },
    {
        id: 'sexuality',
        label: 'Sexual Orientation',
        options: [
            { value: 'heterosexual', label: 'Heterosexual', weight: 0 },
            { value: 'gay_lesbian', label: 'Gay/Lesbian', weight: 2 },
            { value: 'bisexual', label: 'Bisexual', weight: 2 },
            { value: 'asexual', label: 'Asexual', weight: 2 },
            { value: 'pansexual', label: 'Pansexual', weight: 2 },
            { value: 'queer', label: 'Queer', weight: 2 },
        ]
    },
    {
        id: 'disability',
        label: 'Disability Status',
        options: [
            { value: 'no_disability', label: 'No Disability', weight: 0 },
            { value: 'physical_disability', label: 'Physical Disability', weight: 3 },
            { value: 'invisible_disability', label: 'Invisible Disability', weight: 2 },
            { value: 'neurodivergent', label: 'Neurodivergent', weight: 2 },
            { value: 'chronic_illness', label: 'Chronic Illness', weight: 3 },
        ]
    },
    {
        id: 'immigration',
        label: 'Immigration/Citizenship Status',
        options: [
            { value: 'citizen_born', label: 'Born Citizen', weight: 0 },
            { value: 'naturalized', label: 'Naturalized Citizen', weight: 1 },
            { value: 'permanent_resident', label: 'Permanent Resident', weight: 2 },
            { value: 'refugee', label: 'Refugee/Asylee', weight: 3 },
            { value: 'undocumented', label: 'Undocumented', weight: 4 },
        ]
    },
];

const identityMessages: Record<number, string> = {
    1: "You carry a pebble, a relatively small burden. You're not significantly affected by the intersectional elements of your identity.",
    2: "You carry a stone. While noticeable, this weight doesn't severely limit your movement through society.",
    3: "You carry a boulder. This significant weight requires extra effort to navigate many spaces in society.",
    4: "You carry a mountain. The weight of intersecting marginalized identities creates major obstacles across most societal contexts."
};

const defaultIdentityMessage = "You carry no weight. Your societal privilege shields you from the burdens many others face due to their identity."

export const maxIdentityWeightsSum = maxWeightsSum(identityOptions);

export const getIdentityMessage = (bucketIdx: number): string => {
    return identityMessages[bucketIdx] || "The weight of your intersectional identity affects your daily experiences.";
}

export const IdentityViz: React.FC<VizContainerProps> = ({ color, gradientEnd, factors, maxFactorWeightsSum, defaultMessage, getMessage }) => {
    return (
        <VizInner
            color={color}
            gradientEnd={gradientEnd}
            factors={factors}
            maxFactorWeightsSum={maxFactorWeightsSum}
            defaultLabel="Privilege"
            defaultMessage={defaultMessage}
            getMessage={getMessage}
        />
    );
}

export const renderIdentityViz = (identityFactors: Factor[]) => {
    const rockIndex = weightToBucket(sumFactorWeights(identityFactors), maxIdentityWeightsSum);
    const rockColor = `hsl(30, 35%, ${100 - (rockIndex * 10)}%)`;

    return <IdentityViz
        color={rockColor}
        gradientEnd={rockColor}
        factors={identityFactors}
        maxFactorWeightsSum={maxIdentityWeightsSum}
        defaultLabel="Privilege"
        defaultMessage={defaultIdentityMessage}
        getMessage={getIdentityMessage}
    />
};

export const renderIdentityRock = ({ factors }: { factors: Factor[] }) => {
    const idx = weightToBucket(sumFactorWeights(factors), maxIdentityWeightsSum);
    const color = `hsl(30, 35%, ${100 - (idx * 10)}%)`;

    return <RockContainer rockIndex={sumFactorWeights(factors) === 0 ? 0 : idx} color={color} defaultLabel="Privilege" />
}